import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isNullOrUndefined } from 'is-what';
import type { BehaviorSubject, Subscription } from 'rxjs';
import { finalize, Subject } from 'rxjs';

import type { MegaErrors } from '../../mega-exchange/services/interfaces/mega-error';
import { CalendarPickerComponent } from '../../shared/calendar-picker/calendar-picker.component';
import { FooterButtonComponent } from '../../shared/footer-button/footer-button.component';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { MessageService } from '../../shared/services/message.service';
import { UserService } from '../../shared/services/user.service';
import { StringConstants } from '../constants/string-constants';
import { ResponseCodes } from '../response-codes/response-codes';
import { EnterBalancesValidationServiceService } from '../services/enter-balances-validation-service.service';
import type { ExchangeSettleDateRequest } from '../services/interfaces/exchange-settlement-date-request';
import type { ExchangeSettleDateResponse } from '../services/interfaces/exchange-settlement-date-response';
import type { ExpandableSMBSExchangeFeeResponse } from '../services/interfaces/ExpandableSmbsExchangeFeeResponse';
import type { RCRExchangeFeeResponse } from '../services/interfaces/RCRExchangeFeeResponse';
import { PassdataService } from '../services/passdata.service';
import { RcrExchangeNavigationServiceService } from '../services/rcr-exchange-navigation-service.service';
import { RecombinationsService } from '../services/recombinations.service';
import { DateSelectedService } from '../shared/date-selected.service';
import { RcrStickyFooterConfig } from '../shared/sticky-toolbar/rcr-exchange-footer.component';

@Component({
  standalone: false,
  selector: 'invd-enter-balances',
  templateUrl: './enter-balances.component.html',
  styleUrls: ['./enter-balances.component.scss'],
})
export class EnterBalancesComponent implements OnInit, AfterViewInit, OnDestroy {
  public datePickerSource = new Subject<any>();

  public recombinationExchangeResponse: any;
  public dealNumberOrCusip: string;

  public validationStatus = '';

  public SHOW_MODAL_WINDOW: boolean;
  public MODAL_HAS_STATUS_SUCCESS: boolean;
  public MODAL_HAS_STATUS_ERROR: boolean;
  public NO_EXCHANGE_AMOUNTS_ERROR: boolean;

  public statusResolutionMessage: string;
  public statusDisplayMessage: string;
  public netExchangeViews: Array<object>;

  public RCR_FLOW: boolean;
  public SMBS_FLOW: boolean;
  public RCR_FLOW_CIRCULATION_ERROR: boolean;
  public SMBS_FLOW_INTEREST_ERROR: boolean;
  public SMBS_FLOW_INCOMING_OUTGOING_MATCH: boolean;
  public SMBS_FLOW_MODIFY_EXCHANGE: boolean;
  public SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS: boolean;
  // private SMBS_FLOW_PRINCIPAL_INTEREST_MISMATCH: boolean;
  public SMBS_FLOW_PRINCIPAL_MISMATCH: boolean;
  public SMBS_FLOW_INTEREST_MISMATCH: boolean;
  public SMBS_GET_400_ERROR: boolean;
  public RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS: boolean;
  public RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR: boolean;

  public exchangeType: string;
  public clearBtnClick: boolean;
  public steps: Array<string>;
  public currentStep: string;

  public showBanner = false;
  public messageTitle = '';
  public messageBody = '';
  public messageType = '';
  public dealSubTypeForProspectSuppl = '';
  public dealNumberForProspectSuppl = '';
  public prospectSupplURL = '';
  public userSelectedDate = '';
  private showCalenderBusyIndicator = true;
  private calenderDisableDates = [];
  private calendarMinDate: Date = undefined;
  private calendarMaxDate: Date = undefined;
  private subscription: Subscription;
  public showBannerExitIconNavigationPopup = false;
  private isAllowClick = false;
  private isValidateRequest = false;
  public rcrCalcFeeErrorRes: any;
  public totalErrorCount = 0;
  public rcrRemicDealType = '';
  private isPandIAllowClick = false;
  public emailErrorList: any = [];
  private delayDayFlag: any;
  private userStartedEditing = false;

  private rcrFooterContent = [
    new RcrStickyFooterConfig(
      CalendarPickerComponent,
      0,
      'Enter Settlement Date',
      null,
      (value) => {
        this.userSelectedDate = value;
        this.dateSelectedService.broadcastDateSelected(value);
      },
      null,
      {
        disableDates: this.calenderDisableDates,
        busyIndicator: this.showCalenderBusyIndicator,
        minDateValue: this.calendarMinDate,
        maxDateValue: this.calendarMaxDate,
      },
    ),
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      1,
      'Calculate Fee',
      new Date(),
      null,
      (_value) => {
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    ),
  ];
  private smbsFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Clear All Fields',
      null,
      null,
      (_value) => {
        this.onStickyBarClearButtonClick();
      },
      { isPrimary: false },
    ),
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      1,
      'Check Balances',
      null,
      null,
      (_value) => {
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    ),
  ];

  private modifyExchangeFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Modify Exchange',
      null,
      null,
      (_value) => {
        //resetting when user modifies exchange
        this.isAllowClick = false;
        this.isPandIAllowClick = false;
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    ),
  ];
  private modifyTakeDownFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Modify Takedown',
      null,
      null,
      (_value) => {
        this.isAllowClick = false;
        this.isPandIAllowClick = false;
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    ),
  ];

  private enterTakedownFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Review Takedown',
      null,
      null,
      (_value) => {
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    ),
  ];

  private reviewFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'REVIEW',
      null,
      null,
      (_value) => {
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    ),
  ];

  private allowFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Modify Exchange',
      null,
      null,
      (_value) => {
        this.isAllowClick = false;
        this.isPandIAllowClick = false;
        this.onStickyBarButtonClick();
      },
      { isPrimary: false },
    ),
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Allow',
      null,
      null,
      (_value) => {
        this.onStickyBarAllowButtonClick();
      },
      { isPrimary: true },
    ),
  ];

  private allowOnPIFooterContent = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Modify Exchange',
      null,
      null,
      (_value) => {
        this.isPandIAllowClick = false;
        this.isAllowClick = false;
        this.onStickyBarButtonClick();
      },
      { isPrimary: false },
    ),
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Allow',
      null,
      null,
      (_value) => {
        this.onPIAllowButtonClick();
      },
      { isPrimary: true },
    ),
  ];

  public stickFooterComponents: Array<RcrStickyFooterConfig>;

  public isDateMsgHidden = false;

  private rcrService = inject(RecombinationsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private passData = inject(PassdataService);
  public userService = inject(UserService);
  private enterBalancesValidationService = inject(EnterBalancesValidationServiceService);
  private rcrNavigationService = inject(RcrExchangeNavigationServiceService);
  private dateSelectedService = inject(DateSelectedService);
  private fmLoadingPageService = inject(FmLoadingPageService);
  private msgService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = '';
  }

  ngOnInit() {
    this.emailErrorList = [];
    this.passData.setEmailErrors([]);
    this.rcrService?.setRecombinationRequestData(null);
    this.dealNumberOrCusip =
      this.route &&
      this.route.data &&
      (this.route.data as BehaviorSubject<any>).value.recombinations.dealNumber;
    this.recombinationExchangeResponse =
      this.route && this.route.data && (this.route.data as BehaviorSubject<any>).value;
    this.steps = ['Find a Deal', 'Enter Balances', 'Review & Commit', 'Confirmation'];
    this.currentStep = 'Enter Balances';
    this.dealSubTypeForProspectSuppl =
      this.recombinationExchangeResponse &&
      this.recombinationExchangeResponse.recombinations.dealSubType;
    this.dealNumberForProspectSuppl =
      this.recombinationExchangeResponse &&
      this.recombinationExchangeResponse.recombinations.dealNumber;
    this.displayBanner();
    this.displayProspectSupplementURL();
    window.scrollTo(0, 0);
    this.checkSubExchangeType();
    this.listenToBannerExitClick();
    if (!this.userService.isInternalUser) {
      this.passData.setMinOverride(false);
      this.passData.setPndIerrOverride(false);
    }
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    // this.updateFooterForNullOrigFace()
  }

  ngAfterViewInit() {
    if (
      this.recombinationExchangeResponse.recombinations.exchangeType === 'REMIC' &&
      this.recombinationExchangeResponse.recombinations.hasOwnProperty('exchangeSettlementDate')
    ) {
      this.rcrFooterContent = [
        new RcrStickyFooterConfig(
          CalendarPickerComponent,
          0,
          'Enter Settlement Date',
          new Date(this.recombinationExchangeResponse.recombinations.exchangeSettlementDate),
          (value) => {
            this.userSelectedDate = value;
            this.dateSelectedService.broadcastDateSelected(value);
          },
          null,
          {
            disableDates: this.calenderDisableDates,
            busyIndicator: this.showCalenderBusyIndicator,
            minDateValue: this.calendarMinDate,
            maxDateValue: this.calendarMaxDate,
          },
        ),
        new RcrStickyFooterConfig(
          FooterButtonComponent,
          1,
          'Calculate Fee',
          new Date(),
          null,
          (_value) => {
            this.onStickyBarButtonClick();
          },
          { isPrimary: true },
        ),
      ];
      this.stickFooterComponents = this.rcrFooterContent;
      this.getCalanderDate();

      const selectedDate = new Date(
        this.recombinationExchangeResponse.recombinations.exchangeSettlementDate,
      );
      this.userSelectedDate = CalendarPickerComponent.getFormatedDate(selectedDate);

      this.dateSelectedService.broadcastDateSelected(
        CalendarPickerComponent.getFormatedDate(selectedDate),
      );
    } else if (
      this.recombinationExchangeResponse.recombinations.exchangeType ===
        StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS &&
      this.recombinationExchangeResponse.recombinations.hasOwnProperty('exchangeSettlementDate')
    ) {
      this.rcrFooterContent = [
        new RcrStickyFooterConfig(
          CalendarPickerComponent,
          0,
          'Enter Settlement Date',
          new Date(this.recombinationExchangeResponse.recombinations.exchangeSettlementDate),
          (value) => {
            this.userSelectedDate = value;
            this.dateSelectedService.broadcastDateSelected(value);
          },
          null,
          {
            disableDates: this.calenderDisableDates,
            busyIndicator: this.showCalenderBusyIndicator,
            minDateValue: this.calendarMinDate,
            maxDateValue: this.calendarMaxDate,
          },
        ),
        new RcrStickyFooterConfig(
          FooterButtonComponent,
          1,
          'Calculate Fee',
          new Date(),
          null,
          (_value) => {
            this.onStickyBarButtonClick();
          },
          { isPrimary: true },
        ),
      ];
      this.stickFooterComponents = this.rcrFooterContent;
      this.getCalanderDate();

      const selectedDate = new Date(
        this.recombinationExchangeResponse.recombinations.exchangeSettlementDate,
      );
      this.userSelectedDate = CalendarPickerComponent.getFormatedDate(selectedDate);

      this.dateSelectedService.broadcastDateSelected(
        CalendarPickerComponent.getFormatedDate(selectedDate),
      );
    }

    // Trigger change detection to ensure tables render properly
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.enterBalancesValidationService.isCalendarValid = false;
    this.enterBalancesValidationService.isRecombinationTableValid = false;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  canDeactivate(): Promise<boolean> | boolean {
    return true;
  }

  private checkSubExchangeType(): void {
    this.exchangeType =
      this.route &&
      this.route.data &&
      (this.route.data as BehaviorSubject<any>).value.recombinations.exchangeType;
    if (
      this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS ||
      this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS
    ) {
      this.SMBS_FLOW = true;
      this.RCR_FLOW = false;
      this.stickFooterComponents = this.smbsFooterContent;
    } else if (
      this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_PREDEFINED_SMBS ||
      this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_REMIC
    ) {
      this.RCR_FLOW = true;
      this.SMBS_FLOW = false;
      this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS = true;
      this.stickFooterComponents = this.enterTakedownFooterContent;
      this.updateFooterForNullOrigFace();
    } else {
      this.RCR_FLOW = true;
      this.SMBS_FLOW = false;
      this.stickFooterComponents = this.rcrFooterContent;
      this.getCalanderDate();
    }
  }

  public validateScreen(): void {}

  private transformCalculateFeeData(): void {
    this.setDealSettlementDate();
    this.recombinationExchangeResponse.recombinations.sttsTraderId = this.userService.getTraderId();
  }

  private setDealSettlementDate(): void {
    const settlementDate = new Date(this.userSelectedDate);

    if (
      this.exchangeType === StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS ||
      this.exchangeType === StringConstants.EXCHANGE_TYPE_REMIC ||
      this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS
    ) {
      this.recombinationExchangeResponse.recombinations.exchangeSettlementDate =
        settlementDate.getTime();
    } else {
      this.recombinationExchangeResponse.recombinations.exchangeSettlementDate =
        this.recombinationExchangeResponse.recombinations.dealSettlementDate;
    }
  }

  public onStickyBarButtonClick(): void {
    if (!this.MODAL_HAS_STATUS_SUCCESS) {
      this.emailErrorList = [];
      this.passData.setEmailErrors([]);
    }

    if (this.SMBS_FLOW) {
      if (this.SMBS_FLOW_INTEREST_ERROR) {
        //this.SHOW_MODAL_WINDOW = false;
        this.updateStickyFooter();
      } else if (this.validationStatus === ResponseCodes.VALIDATION_EXCHANGE_STATUSES.SUCCESS) {
        //this.SHOW_MODAL_WINDOW = false;
        /*
         * SSSECSTR01-2890 -- Removing logic for setting false flag for SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS
         * This was causing an issue as checkBalance method is not getting called whenever Modify Exchange is clicked.
         * Instead it was making a call to calculateFee() which is not correct.
         */
        this.updateStickyFooter();
        if (this.NO_EXCHANGE_AMOUNTS_ERROR) {
          this.SHOW_MODAL_WINDOW = false;
          this.MODAL_HAS_STATUS_SUCCESS = false;
          this.MODAL_HAS_STATUS_ERROR = false;
          this.NO_EXCHANGE_AMOUNTS_ERROR = false;
          this.SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS = true;
        } else if (this.SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS) {
          this.SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS = false;
          this.checkBalance();
        } else {
          this.calculateFee();
        }
      } else if (
        this.validationStatus === ResponseCodes.VALIDATION_EXCHANGE_STATUSES.INTEREST_MISMATCH
      ) {
        this.SHOW_MODAL_WINDOW = false;
        this.stickFooterComponents = this.smbsFooterContent;
        this.validationStatus = StringConstants.EMPTY_STRING;
      } else if (
        this.validationStatus === ResponseCodes.VALIDATION_EXCHANGE_STATUSES.PRINCIPAL_MISMATCH
      ) {
        this.SHOW_MODAL_WINDOW = false;
        this.stickFooterComponents = this.smbsFooterContent;
        this.validationStatus = StringConstants.EMPTY_STRING;
      } else if (
        this.validationStatus ===
        ResponseCodes.VALIDATION_EXCHANGE_STATUSES.PRINCIPAL_INTEREST_MISMATCH
      ) {
        this.SHOW_MODAL_WINDOW = false;
        this.validationStatus = StringConstants.EMPTY_STRING;
        this.updateStickyFooterForPrincipalInterestMistmatch();
      } else if (this.SMBS_GET_400_ERROR) {
        this.SHOW_MODAL_WINDOW = false;
        this.stickFooterComponents = this.smbsFooterContent;
        this.SMBS_GET_400_ERROR = false;
      } else {
        this.checkBalance();
      }
    }

    if (this.RCR_FLOW) {
      if (this.validationStatus === ResponseCodes.VALIDATION_EXCHANGE_STATUSES.INTEREST_MISMATCH) {
        this.SHOW_MODAL_WINDOW = false;
        this.validationStatus = StringConstants.EMPTY_STRING;
        this.dismissPoupMessageWindow();
        return;
      } else if (
        this.validationStatus === ResponseCodes.VALIDATION_EXCHANGE_STATUSES.PRINCIPAL_MISMATCH
      ) {
        this.SHOW_MODAL_WINDOW = false;
        this.validationStatus = StringConstants.EMPTY_STRING;
        this.dismissPoupMessageWindow();

        return;
      } else if (
        this.validationStatus ===
        ResponseCodes.VALIDATION_EXCHANGE_STATUSES.PRINCIPAL_INTEREST_MISMATCH
      ) {
        this.SHOW_MODAL_WINDOW = false;
        this.validationStatus = StringConstants.EMPTY_STRING;
        this.dismissPoupMessageWindow();
        return;
      } else if (this.SMBS_GET_400_ERROR) {
        this.SHOW_MODAL_WINDOW = false;
        this.dismissPoupMessageWindow();
        this.SMBS_GET_400_ERROR = false;
      }

      if (this.RCR_FLOW_CIRCULATION_ERROR) {
        this.SHOW_MODAL_WINDOW = false;
        this.updateStickyFooter();
      } else if (this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS) {
        if (this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR) {
          this.SHOW_MODAL_WINDOW = false;
          this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR = false;

          if (
            this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_REMIC ||
            this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_PREDEFINED_SMBS
          ) {
            this.updateStickyFooterForAtSettlementOnMininumCheck();
          } else {
            this.updateStickyFooterForAtSettlementOnRetry();
          }
        } else if (this.NO_EXCHANGE_AMOUNTS_ERROR) {
          //#TODO check what type of flow it is
          this.SHOW_MODAL_WINDOW = false;
          this.MODAL_HAS_STATUS_SUCCESS = false;
          this.MODAL_HAS_STATUS_ERROR = false;
          this.NO_EXCHANGE_AMOUNTS_ERROR = false;
          //#TODO update sticky footer text to reflect BE is not giving us correct flags
          this.calculateFee();
        } else {
          this.calculateFee();
        }
      } else {
        this.calculateFee();
      }
    }
  }

  public takedownFlowUpdateFooter(): void {
    if (this.exchangeType !== StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS) {
      this.updateStickyFooterForAtSettlementOnMininumCheck();
    }
  }

  public onStickyBarAllowButtonClick(): void {
    this.isAllowClick = true;
    this.RCR_FLOW_CIRCULATION_ERROR = false;
    if (this.isValidateRequest) {
      this.isValidateRequest = false;
      this.checkBalance();
    } else {
      this.calculateFee();
    }
  }

  public onPIAllowButtonClick(): void {
    this.isPandIAllowClick = true;
    if (this.isValidateRequest) {
      this.isValidateRequest = false;
      this.checkBalance();
    } else {
      this.calculateFee();
    }
  }

  public onStickyBarClearButtonClick(): void {
    this.clearBtnClick = !this.clearBtnClick;
  }

  public calculateFee(): void {
    this.transformCalculateFeeData();
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.rcrService.calculateFee(this.removeNullRecombinationGroup()).subscribe({
      next: (calculateFeeResponse) =>
        this.onNextcalculateFeeResponse(calculateFeeResponse as RCRExchangeFeeResponse),
      error: (error) => this.onErrorCalculateFeeResponse(error),
    });
  }

  private getCalanderDate(): void {
    if (
      (this.recombinationExchangeResponse.recombinations.exchangeType ===
        StringConstants.EXCHANGE_TYPE_SMBS ||
        this.recombinationExchangeResponse.recombinations.exchangeType ===
          StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS) &&
      this.recombinationExchangeResponse.recombinations.hasOwnProperty('exchangeSettlementDate')
    ) {
      this.rcrFooterContent[0].config.busyIndicator = false;
      this.showCalenderBusyIndicator = false;
    } else {
      this.rcrFooterContent[0].config.busyIndicator = true;
      this.showCalenderBusyIndicator = true;
      this.cdr.markForCheck();
    }
    this.delayDayFlag = this.rcrService.getExpandableSMBSExchangeResponse();
    const getCalanderServiceReq = {
      exchangeType: this.exchangeType,
      dealNumber: this.recombinationExchangeResponse.recombinations.dealNumber,
      dealSubType: this.recombinationExchangeResponse.recombinations.dealSubType,
      delayDayFlag: this.delayDayFlag?.delayDayFlag ?? false,
    } as ExchangeSettleDateRequest;
    this.rcrService
      .getExchangeSettlementDateService(getCalanderServiceReq)
      .pipe(finalize(() => this.clearCalendarBusyIndicator()))
      .subscribe({
        next: (getCalanderResponse) => {
          this.onGetSettlementDateSuccess(getCalanderResponse as ExchangeSettleDateResponse);
        },
        error: (error) => {
          this.onGetSettlementDateError(error);
        },
      });
  }

  private onGetSettlementDateSuccess(res: ExchangeSettleDateResponse): void {
    this.calenderDisableDates = [];
    res.exclusions.map((dataStr) => {
      const dtArr = dataStr.split('-');
      const dateObj = new Date(dtArr[1] + '/' + dtArr[2] + '/' + dtArr[0]);
      this.calenderDisableDates.push(dateObj);
    });
    const startDateArr = res.startDate.split('-');
    const endDateArr = res.endDate.split('-');
    this.calendarMinDate = new Date(
      startDateArr[1] + '/' + startDateArr[2] + '/' + startDateArr[0],
    );
    this.calendarMaxDate = new Date(endDateArr[1] + '/' + endDateArr[2] + '/' + endDateArr[0]);
    this.rcrFooterContent[0].config.disableDates = this.calenderDisableDates;
    this.rcrFooterContent[0].config.minDateValue = this.calendarMinDate;
    this.rcrFooterContent[0].config.maxDateValue = this.calendarMaxDate;
    this.handleEarlyCloseCase(res.earlyClose);
    this.cdr.markForCheck();
  }

  private onGetSettlementDateError(_errorRes: any): void {
    this.cdr.markForCheck();
  }

  private clearCalendarBusyIndicator(): void {
    this.rcrFooterContent[0].config.busyIndicator = false;
    this.showCalenderBusyIndicator = false;
    this.cdr.markForCheck();
  }

  private handleEarlyCloseCase(strEarlyClose: string[]): void {
    this.subscription = this.msgService.getMessage().subscribe((message) => {
      const newDate = new Date(message.text);
      const mMonth = newDate.getMonth() + 1;
      let mStrMonth: string;
      if (mMonth < 10) {
        mStrMonth = '0' + mMonth;
      } else {
        mStrMonth = mMonth.toString();
      }
      const mSelDate = newDate.getFullYear() + '-' + mStrMonth + '-' + newDate.getDate();
      if (strEarlyClose != null && strEarlyClose.length > 0) {
        strEarlyClose.forEach((item) => {
          if (mSelDate === item) {
            this.isDateMsgHidden = true;
          }
        });
      }
    });
  }

  private formatExclusionDates(dateExclusions: Array<string>): Array<any> {
    const exclusionDates: Array<any> = new Array<any>();

    for (const dateExclusion of dateExclusions) {
      const convertedDateArray = dateExclusion.split('-');
      const convertedDate = new Date(
        Number(convertedDateArray[0]),
        Number(convertedDateArray[1]) - 1,
        Number(convertedDateArray[2]),
      );
      exclusionDates.push({
        year: convertedDate.getFullYear(),
        month: convertedDate.getMonth() + 1,
        day: convertedDate.getDate(),
      });
    }

    return exclusionDates;
  }

  private onNextcalculateFeeResponse(rcrFeeResponseObject: any): void {
    if (rcrFeeResponseObject.exchangeValidationStatus === 'Failed') {
      this.calcFeeBalanceErrorScreen(rcrFeeResponseObject);
    } else {
      this.SHOW_MODAL_WINDOW = false;
      this.rcrService.setRCRExchangeFeeResponse(rcrFeeResponseObject);
      this.passData.setExchangeType(this.exchangeType);
      this.passData.setRecombinationDataCache(this.recombinationExchangeResponse);
      this.passData.setRecombinationDataCacheWithoutNullGroup(this.removeNullRecombinationGroup());
      this.rcrNavigationService.canNavigate = true;
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.isAllowClick = false; //Resetting the value to default
      this.isPandIAllowClick = false;
      this.router.navigate(['./', 'review-and-commit'], { relativeTo: this.route });
    }
  }

  public calcFeeBalanceErrorScreen(rcrFeeResponseObject: any): void {
    this.validationStatus = rcrFeeResponseObject.netExchangeViews[0].validationStatus;
    rcrFeeResponseObject.netExchangeViews.forEach((item) => {
      let errorMsg = '';
      let tempErrorMsg = '';

      this.recombinationExchangeResponse.recombinations.recombinations.forEach((itemError) => {
        if (itemError.recombinationNumber === item.recombinationNumber) {
          tempErrorMsg = item.displayMessage && item.displayMessage.split('.')[0];

          errorMsg =
            tempErrorMsg +
            ' for ' +
            'Group ' +
            itemError.recombinationCollatGroup +
            ' - ' +
            'Recombination ' +
            itemError.recombinationNumber +
            ' .';
          this.createEmailError(errorMsg);
        }
      });
    });

    this.resetModalStatus();
    this.SMBS_GET_400_ERROR = false;
    this.NO_EXCHANGE_AMOUNTS_ERROR = false;
    this.SHOW_MODAL_WINDOW = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.rcrCalcFeeErrorRes = rcrFeeResponseObject;
    this.rcrRemicDealType = rcrFeeResponseObject.initialDealType;

    if (!isNullOrUndefined(this.rcrCalcFeeErrorRes)) {
      this.totalErrorCount = this.rcrCalcFeeErrorRes.netExchangeViews.length;
    }

    if (this.userService.isInternalUser) {
      this.stickFooterComponents = this.allowOnPIFooterContent;
    } else {
      this.stickFooterComponents = this.modifyExchangeFooterContent;
    }
  }

  private onErrorCalculateFeeResponse(error: any): void {
    this.isAllowClick = false; //Resetting the value to default
    this.rcrNavigationService.canNavigate = false;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    const errorObject: MegaErrors = error && error.error;
    this.resetModalStatus();
    this.SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS = true;
    this.SHOW_MODAL_WINDOW = true;

    if (this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS) {
      if (this.exchangeType !== StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS) {
        this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR = true;
      }
      this.RCR_FLOW_CIRCULATION_ERROR = false;
    } else {
      this.RCR_FLOW_CIRCULATION_ERROR = true;
    }

    this.fJ5CAT1002ErrorScenario(errorObject);

    // At Settlement Sticky Footer
    if (
      (errorObject.messages[0].messageCode === 'FJ5CAT_1009' ||
        errorObject.messages[0].messageCode === 'FJ5CAT_1006' ||
        errorObject.messages[0].messageCode === 'FJ5CAT_1010') &&
      this.userService.isRCRInternal()
    ) {
      this.stickFooterComponents = this.allowFooterContent;
    }

    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  private fJ5CAT1002ErrorScenario(error: MegaErrors): void {
    // Show Modal on Error
    let tempStatusDisplayMessage = '';
    this.MODAL_HAS_STATUS_ERROR = true;
    // Update Sticky Footer #TODO refactor in function
    this.stickFooterComponents = this.modifyExchangeFooterContent;
    this.statusDisplayMessage = error.messages[0].errorMessage;
    tempStatusDisplayMessage =
      this.statusDisplayMessage && this.statusDisplayMessage.split('Click')[0];
    this.createEmailError(tempStatusDisplayMessage);

    // SMBS Flow
    this.SMBS_FLOW_INTEREST_ERROR = false;
  }

  private updateStickyFooter(): void {
    if (this.SMBS_FLOW) {
      if (this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS) {
        if (this.NO_EXCHANGE_AMOUNTS_ERROR) {
          this.stickFooterComponents = this.smbsFooterContent;
          this.SMBS_FLOW_PRINCIPAL_MISMATCH = false;
          this.SMBS_FLOW_INTEREST_MISMATCH = false;
        } else if (
          this.recombinationExchangeResponse.recombinations.exchangeType ===
            StringConstants.EXCHANGE_TYPE_SMBS &&
          this.recombinationExchangeResponse.recombinations.hasOwnProperty(
            'exchangeSettlementDate',
          ) &&
          !isNaN(this.recombinationExchangeResponse.recombinations.exchangeSettlementDate)
        ) {
          this.rcrFooterContent[0].value = this.userSelectedDate;
          this.stickFooterComponents = this.rcrFooterContent;
          this.getCalanderDate();
        } else {
          this.stickFooterComponents = this.rcrFooterContent;
          this.getCalanderDate();
        }
      } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS) {
        this.stickFooterComponents = this.enterTakedownFooterContent;
      }
    }

    if (this.RCR_FLOW) {
      if (this.RCR_FLOW_CIRCULATION_ERROR) {
        this.stickFooterComponents = this.rcrFooterContent;
        this.getCalanderDate();
        this.RCR_FLOW_CIRCULATION_ERROR = false;
      } else if (this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS) {
        this.smbsFooterContent = this.reviewFooterContent;
      }
    }
  }

  private updateStickyFooterForPrincipalInterestMistmatch(): void {
    if (
      this.SMBS_FLOW && // included type "AT-SETTLE-EXPD-SMBS" for defect SSSECSTR01-3306
      (this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS ||
        this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS)
    ) {
      this.stickFooterComponents = this.smbsFooterContent;
      this.SMBS_FLOW_PRINCIPAL_MISMATCH = false;
      this.SMBS_FLOW_INTEREST_MISMATCH = false;
    }
  }

  // #TODO BE is not sending correct flag to distinguish this error
  // we need specific Error Codes for each case
  private updateStickyFooterForAtSettlementOnMininumCheck(): void {
    this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR = false;
    this.stickFooterComponents = this.enterTakedownFooterContent;
  }

  private updateStickyFooterForAtSettlementOnRetry(): void {
    this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR = false;
    this.smbsFooterContent = this.reviewFooterContent;
  }

  private updateStickyFooterForAtSettlementOnError(): void {
    if (
      this.RCR_FLOW &&
      (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_PREDEFINED_SMBS ||
        this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_REMIC)
    ) {
      this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS_ERROR = true;
      this.stickFooterComponents = this.modifyExchangeFooterContent;
    }
  }

  private updateStickyFooterForAtRCROnError(): void {
    if (this.RCR_FLOW && this.exchangeType === StringConstants.EXCHANGE_TYPE_REMIC) {
      this.RCR_FLOW_CIRCULATION_ERROR = true;
      this.stickFooterComponents = this.modifyExchangeFooterContent;
    }
  }

  public updateStickyFooterForRCRCirculationOnError(): void {
    if (
      this.RCR_FLOW &&
      this.RCR_FLOW_CIRCULATION_ERROR &&
      this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS
    ) {
      this.stickFooterComponents = this.modifyExchangeFooterContent;
    }
  }

  private updateStickyFooterForSMBSOnRetry(): void {
    if (this.SMBS_FLOW && this.SMBS_FLOW_INTEREST_MISMATCH && this.SMBS_FLOW_PRINCIPAL_MISMATCH) {
      if (this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS) {
        this.stickFooterComponents = this.modifyExchangeFooterContent;
      } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS) {
        this.stickFooterComponents = this.modifyTakeDownFooterContent;
      }
    }
  }

  public updateStickyfooterForRCR(): void {
    this.stickFooterComponents = this.modifyExchangeFooterContent;
  }

  private checkBalance(): void {
    this.recombinationExchangeResponse.recombinations.sttsTraderId = this.userService.getTraderId();
    this.fmLoadingPageService.isLoaderRequesting.set(true);

    this.clearCommasForRecommbinations();

    this.passData.setMinOverride(this.isAllowClick);
    this.passData.setPndIerrOverride(this.isPandIAllowClick);

    this.recombinationExchangeResponse.recombinations.isMinOverride =
      this.passData.getMinOverride();
    this.recombinationExchangeResponse.recombinations.isPndIerrOverride =
      this.passData.getPndIerrOverride();
    if (
      this.recombinationExchangeResponse.recombinations.hasOwnProperty('exchangeSettlementDate') &&
      !isNaN(this.recombinationExchangeResponse.recombinations.exchangeSettlementDate)
    ) {
      const selectedDate = new Date(
        this.recombinationExchangeResponse.recombinations.exchangeSettlementDate,
      );
      this.userSelectedDate = CalendarPickerComponent.getFormatedDate(selectedDate);
      this.dateSelectedService.broadcastDateSelected(
        CalendarPickerComponent.getFormatedDate(selectedDate),
      );
    } else if (this.rcrFooterContent[0].value != null) {
      this.rcrFooterContent[0].value = this.userSelectedDate;
      this.stickFooterComponents = this.rcrFooterContent;
      this.getCalanderDate();
    }
    this.rcrService.checkSMBSBalances(this.recombinationExchangeResponse.recombinations).subscribe({
      next: (smbsFeeResponse) =>
        this.onNextCheckBalanceResponse(smbsFeeResponse as ExpandableSMBSExchangeFeeResponse),
      error: (error) => this.onNextCheckBalanceError(error),
    });
  }

  private clearCommasForRecommbinations(): void {
    for (const mRecom of this.recombinationExchangeResponse.recombinations.recombinations) {
      if (mRecom.recombinationRows != null) {
        for (const recomRow of mRecom.recombinationRows) {
          if (
            !isNullOrUndefined(recomRow.origFaceToDealer) &&
            recomRow.origFaceToDealer.toString() !== '0'
          ) {
            recomRow.origFaceToDealer = this.clearSeparators(recomRow.origFaceToDealer.toString());
          }
          if (
            !isNullOrUndefined(recomRow.origFaceToFNMA) &&
            recomRow.origFaceToFNMA.toString() !== '0'
          ) {
            recomRow.origFaceToFNMA = this.clearSeparators(recomRow.origFaceToFNMA.toString());
          }
        }
      }
    }
  }

  private clearSeparators(inputNumber: string): string {
    return inputNumber.replace(/,/g, '');
  }

  private onNextCheckBalanceResponse(
    smbsFeeResponseObject: ExpandableSMBSExchangeFeeResponse,
  ): void {
    this.rcrService.setExpandableSMBSExchangeResponse(smbsFeeResponseObject);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.validationStatus = smbsFeeResponseObject.validationStatus;
    this.statusDisplayMessage = '';
    this.statusResolutionMessage = '';
    let tempErrorMsg = '';

    this.resetModalStatus();
    this.SMBS_GET_400_ERROR = false;
    this.NO_EXCHANGE_AMOUNTS_ERROR = false;

    if (
      smbsFeeResponseObject.validationStatus ===
      ResponseCodes.VALIDATION_EXCHANGE_STATUSES.PRINCIPAL_MISMATCH
    ) {
      this.MODAL_HAS_STATUS_ERROR = true;
      this.SHOW_MODAL_WINDOW = true;
      this.SMBS_FLOW_PRINCIPAL_MISMATCH = true;
      this.SMBS_FLOW_INTEREST_MISMATCH = false;
      this.statusDisplayMessage = smbsFeeResponseObject.displayMessage;
      this.statusResolutionMessage = smbsFeeResponseObject.resolution;
      tempErrorMsg = this.statusDisplayMessage;
      this.createEmailError(tempErrorMsg);
      this.netExchangeViews = smbsFeeResponseObject.netExchangeViews;
      if (this.userService.isInternalUser) {
        this.updateStickyFooterForInternalSMBSOnPIERROR();
      } else {
        this.updateStickyFooterForSmbsOnError();
      }
    } else if (
      smbsFeeResponseObject.validationStatus === ResponseCodes.VALIDATION_EXCHANGE_STATUSES.SUCCESS
    ) {
      this.SHOW_MODAL_WINDOW = true;
      this.SMBS_FLOW_INTEREST_ERROR = false;
      this.MODAL_HAS_STATUS_SUCCESS = true;
      this.SMBS_FLOW_INCOMING_OUTGOING_MATCH = true;
      this.SMBS_FLOW_MODIFY_EXCHANGE = true;
      this.SMBS_FLOW_PRINCIPAL_MISMATCH = false;
      this.SMBS_FLOW_INTEREST_MISMATCH = false;
      this.statusDisplayMessage = smbsFeeResponseObject.displayMessage;
      this.statusResolutionMessage = smbsFeeResponseObject.resolution;
      this.netExchangeViews = smbsFeeResponseObject.netExchangeViews;
      this.updateStickyFooter();
    } else if (
      smbsFeeResponseObject.validationStatus ===
      ResponseCodes.VALIDATION_EXCHANGE_STATUSES.INTEREST_MISMATCH
    ) {
      this.MODAL_HAS_STATUS_ERROR = true;
      this.SHOW_MODAL_WINDOW = true;
      this.SMBS_FLOW_INTEREST_MISMATCH = true;
      this.SMBS_FLOW_PRINCIPAL_MISMATCH = false;
      this.statusDisplayMessage = smbsFeeResponseObject.displayMessage;
      this.statusResolutionMessage = smbsFeeResponseObject.resolution;
      tempErrorMsg = this.statusDisplayMessage;
      this.createEmailError(tempErrorMsg);
      this.netExchangeViews = smbsFeeResponseObject.netExchangeViews;
      if (this.userService.isInternalUser) {
        this.updateStickyFooterForInternalSMBSOnPIERROR();
      } else {
        this.updateStickyFooterForSmbsOnError();
      }
    } else if (
      smbsFeeResponseObject.validationStatus ===
      ResponseCodes.VALIDATION_EXCHANGE_STATUSES.PRINCIPAL_INTEREST_MISMATCH
    ) {
      this.SHOW_MODAL_WINDOW = true;
      this.MODAL_HAS_STATUS_ERROR = true;
      this.SMBS_FLOW_MODIFY_EXCHANGE = false;
      this.SMBS_FLOW_INTEREST_MISMATCH = true;
      this.SMBS_FLOW_PRINCIPAL_MISMATCH = true;
      this.statusDisplayMessage = smbsFeeResponseObject.displayMessage;
      this.statusResolutionMessage = smbsFeeResponseObject.resolution;
      tempErrorMsg = this.statusDisplayMessage;
      this.createEmailError(tempErrorMsg);
      this.netExchangeViews = smbsFeeResponseObject.netExchangeViews;
      if (this.userService.isInternalUser) {
        this.updateStickyFooterForInternalSMBSOnPIERROR();
      } else {
        this.updateStickyFooterForSMBSOnRetry();
      }
    }
    this.cdr.markForCheck();
  }

  private resetModalStatus(): void {
    this.MODAL_HAS_STATUS_SUCCESS = false;
    this.MODAL_HAS_STATUS_ERROR = false;
  }

  private onNextCheckBalanceError(error: any) {
    let tempStatusDisplayMessage = '';
    this.statusDisplayMessage = '';
    this.statusResolutionMessage = '';
    const errorObject: MegaErrors = error && error.error;
    this.SHOW_MODAL_WINDOW = true;
    this.MODAL_HAS_STATUS_SUCCESS = false;
    this.MODAL_HAS_STATUS_ERROR = true;
    this.statusDisplayMessage = errorObject.messages[0].errorMessage;
    tempStatusDisplayMessage =
      this.statusDisplayMessage && this.statusDisplayMessage.split('Click')[0];
    this.createEmailError(tempStatusDisplayMessage);
    this.SMBS_FLOW_INCOMING_OUTGOING_MATCH = false;
    this.SMBS_GET_400_ERROR = true;
    this.netExchangeViews = null;
    this.validationStatus = ResponseCodes.VALIDATION_EXCHANGE_STATUSES.ERROR;

    if (
      (errorObject.messages[0].messageCode === 'FJ5CAT_1009' ||
        errorObject.messages[0].messageCode === 'FJ5CAT_1006' ||
        errorObject.messages[0].messageCode === 'FJ5CAT_1010') &&
      this.userService.isRCRInternal()
    ) {
      this.isValidateRequest = true;
      this.stickFooterComponents = this.allowFooterContent;
    } else if (
      !this.userService.isRCRInternal() ||
      errorObject.messages[0].messageCode === 'FJ5CAT_1002'
    ) {
      // this.updateStickyFooterForSmbsOnError();
      this.fJ5CAT1002ErrorScenario(errorObject);
    }

    // this.updateStickyFooterForSmbsOnError();
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  private updateStickyFooterForSmbsOnError(): void {
    if (this.SMBS_FLOW) {
      if (this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS) {
        this.stickFooterComponents = this.modifyExchangeFooterContent;
      } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS) {
        this.stickFooterComponents = this.modifyTakeDownFooterContent;
      }
    }
  }

  protected dismissPoupMessageWindow(): void {
    if (this.SMBS_FLOW) {
      if (
        this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS ||
        this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS
      ) {
        this.isAllowClick = false;
        this.isPandIAllowClick = false;
        this.SHOW_MODAL_WINDOW = false;
        this.SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS = true;
        this.stickFooterComponents = this.smbsFooterContent;
      }
    } else if (this.RCR_FLOW) {
      this.SHOW_MODAL_WINDOW = false;
      this.SMBS_FLOW_INCOMING_OUTGOING_MATCH_ADJUSTMENTS = true;
      this.validationStatus = StringConstants.EMPTY_STRING;
      this.RCR_FLOW_CIRCULATION_ERROR = false;
      if (this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS) {
        this.takedownFlowUpdateFooter();
      } else {
        this.stickFooterComponents = this.rcrFooterContent;
      }
    }
  }

  private showModalMessage(): void {
    this.router.navigate(
      [
        {
          outlets: {
            primary: this.route,
            popupMessage: '(popupMessage:review-message)',
          },
        },
      ],
      { skipLocationChange: true, relativeTo: this.route },
    );
  }

  private closeModalMessage(): void {
    this.router.navigate(
      [
        {
          outlets: {
            primary: this.route,
            popupMessage: null,
          },
        },
      ],
      { skipLocationChange: true, relativeTo: this.route },
    );
  }

  private removeNullRecombinationGroup() {
    const recombinationGroupCopy: any = new Object();
    recombinationGroupCopy.dealNumber =
      this.recombinationExchangeResponse.recombinations.dealNumber;
    recombinationGroupCopy.dealSettlementDate =
      this.recombinationExchangeResponse.recombinations.dealSettlementDate;
    recombinationGroupCopy.dealSubType =
      this.recombinationExchangeResponse.recombinations.dealSubType;
    recombinationGroupCopy.dealType = this.recombinationExchangeResponse.recombinations.dealType;
    recombinationGroupCopy.exchangeSettlementDate =
      this.recombinationExchangeResponse.recombinations.exchangeSettlementDate;
    recombinationGroupCopy.exchangeType =
      this.recombinationExchangeResponse.recombinations.exchangeType;
    recombinationGroupCopy.invDirUserId =
      this.recombinationExchangeResponse.recombinations.invDirUserId;
    recombinationGroupCopy.sessionToken =
      this.recombinationExchangeResponse.recombinations.sessionToken;
    recombinationGroupCopy.sttsTraderId =
      this.recombinationExchangeResponse.recombinations.sttsTraderId;

    this.passData.setMinOverride(this.isAllowClick);
    this.passData.setPndIerrOverride(this.isPandIAllowClick);
    recombinationGroupCopy.isMinOverride = this.passData.getMinOverride();
    recombinationGroupCopy.isPndIerrOverride = this.passData.getPndIerrOverride();

    const recomArray: any[] = [];

    this.recombinationExchangeResponse.recombinations.recombinations.forEach((item) => {
      recomArray.push(item);
    });

    recombinationGroupCopy.recombinations = recomArray;
    return recombinationGroupCopy;
  }

  private displayBanner(): void {
    //this.showBanner = this.userService.isInternal; #TODO when login is working
    this.showBanner = true;
    this.messageTitle = 'Notice';
    this.messageBody = `You are currently making an exchange for ${this.userService.getTraderName()} at ${this.userService.getDealerName()}`;
    this.messageType = StringConstants.MESSAGE_NOTICE;
  }

  private listenToDatePicker(): void {
    this.dateSelectedService.dateSelected$.subscribe((_dateSelected) => {
      this.enterBalancesValidationService.isCalendarValid = true;
    });
  }

  public listenToBannerExitClick(): void {
    this.dateSelectedService.bannerExitClickInEnterBalances$.subscribe((_exitClicked) => {
      this.showBannerExitIconNavigationPopup = true;
    });
  }

  protected goToRCRRequestExchangePage(): void {
    this.router.navigate(['rcr-exchange/lookup']);
  }

  private displayProspectSupplementURL(): void {
    if (this.dealNumberForProspectSuppl.includes('M')) {
      this.prospectSupplURL = 'https://mfdusdisclose.fanniemae.com/#/home';
    } else if (this.dealSubTypeForProspectSuppl === 'SMBS') {
      this.prospectSupplURL =
        'https://fanniemae.mbs-securities.com/fannie/deal/' +
        'TR000' +
        this.dealNumberForProspectSuppl;
    } else {
      this.prospectSupplURL =
        'https://fanniemae.mbs-securities.com/fannie/deal/' + this.dealNumberForProspectSuppl;
    }
  }

  private updateStickyFooterForInternalSMBSOnPIERROR(): void {
    // just let internal user to allow override
    this.isValidateRequest = true;
    this.stickFooterComponents = this.allowOnPIFooterContent;
  }

  public createEmailError(msg: any): void {
    this.emailErrorList.push(msg);
    this.passData.setEmailErrors(this.emailErrorList);
  }

  private updateFooterForNullOrigFace(): void {
    const allNull = this.recombinationExchangeResponse?.recombinations?.recombinations?.every(
      (recom) =>
        recom.recombinationRows?.every(
          (row) => row.origFaceToDealer == null && row.origFaceToFNMA == null,
        ),
    );
    if (allNull) {
      this.stickFooterComponents = [
        new RcrStickyFooterConfig(
          FooterButtonComponent,
          0,
          'Submit Without Takedowns',
          null,
          null,
          (_value) => {
            this.submitWithoutTakedown();
          },
          { isPrimary: true },
        ),
      ];
    }
  }

  protected submitWithoutTakedown(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const payload = {
      dealNumber: this.dealNumberOrCusip,
      noTakedownFlag: true,
      isUserUdpate: true,
    };
    this.rcrService.sendNoTakedownEmail(payload).subscribe({
      next: () => this.handleSendNoTakedownEmailResponse(),
      error: () => this.handleSendNoTakedownEmailResponseError(),
    });
  }

  handleSendNoTakedownEmailResponse(): void {
    this.fmLoadingPageService.stopLoading();
    this.router.navigate(['rcr-exchange/rcr-takedown']);
  }

  handleSendNoTakedownEmailResponseError(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public onAnyRowStartedEditing(): void {
    if (!this.userStartedEditing) {
      if (this.RCR_FLOW && !this.RCR_FLOW_AT_SETTLEMENT_TAKEDOWNS) {
        this.stickFooterComponents = this.createRcrFooterForEditing();
        this.getCalanderDate();
      } else {
        this.stickFooterComponents = this.enterTakedownFooterContent;
      }
      this.userStartedEditing = true;
    }
  }

  private createRcrFooterForEditing(): RcrStickyFooterConfig[] {
    const currentButtonText = this.getCurrentButtonText();
    const currentCalendarValue = this.rcrFooterContent[0].value;

    return [
      this.createCalendarPickerConfig(currentCalendarValue),
      this.createFooterButtonConfig(currentButtonText),
    ];
  }

  private getCurrentButtonText(): string {
    return this.stickFooterComponents && this.stickFooterComponents.length > 1
      ? this.stickFooterComponents[1].labelField
      : 'Calculate Fee';
  }

  private createCalendarPickerConfig(value: any): RcrStickyFooterConfig {
    return new RcrStickyFooterConfig(
      CalendarPickerComponent,
      0,
      'Enter Settlement Date',
      value,
      (selectedValue) => {
        this.userSelectedDate = selectedValue;
        this.dateSelectedService.broadcastDateSelected(selectedValue);
      },
      null,
      {
        disableDates: this.calenderDisableDates,
        busyIndicator: this.showCalenderBusyIndicator,
        minDateValue: this.calendarMinDate,
        maxDateValue: this.calendarMaxDate,
      },
    );
  }

  private createFooterButtonConfig(buttonText: string): RcrStickyFooterConfig {
    return new RcrStickyFooterConfig(
      FooterButtonComponent,
      1,
      buttonText,
      null,
      null,
      (_value) => {
        this.onStickyBarButtonClick();
      },
      { isPrimary: true },
    );
  }
}
